import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faCheck,
  faFlag,
  faTriangleExclamation,
} from '@fortawesome/pro-duotone-svg-icons'

import {
  LeaseClauseCheckResult,
  LeaseClauseSeverity,
} from '../../_types/quickpass-llm'
import { JurisdictionOption, LeaseCheckCacheEntry } from './types'

const CACHE_PREFIX = 'quickpass.leaseCheck:v1:'
const CACHE_TTL_MS = 6 * 60 * 60 * 1000
const CACHE_MAX_KEYS = 5
const FINGERPRINT_PREFIX_LENGTH = 600
const RISK_GREEN_MAX = 25
const RISK_YELLOW_MAX = 60

const JURISDICTION_OPTIONS: JurisdictionOption[] = [
  { value: 'Ontario, Canada', label: 'Ontario' },
  { value: 'British Columbia, Canada', label: 'British Columbia' },
  { value: 'Quebec, Canada', label: 'Quebec' },
  { value: 'Alberta, Canada', label: 'Alberta' },
  { value: 'Manitoba, Canada', label: 'Manitoba' },
  { value: 'Nova Scotia, Canada', label: 'Nova Scotia' },
  { value: 'New Brunswick, Canada', label: 'New Brunswick' },
  { value: 'Saskatchewan, Canada', label: 'Saskatchewan' },
  { value: 'Newfoundland and Labrador, Canada', label: 'Newfoundland' },
  { value: 'Prince Edward Island, Canada', label: 'PEI' },
  { value: 'Canada (general)', label: 'Other / not sure' },
]

const buildFingerprint = (leaseText: string, jurisdiction: string): string => {
  const normalised = leaseText.replace(/\s+/gu, ' ').trim()
  const lengthMarker = `len:${normalised.length}`
  const prefix = normalised.slice(0, FINGERPRINT_PREFIX_LENGTH)
  return `${jurisdiction}::${lengthMarker}::${prefix}`
}

const trimCacheToMax = (): void => {
  try {
    const allKeys: string[] = []
    for (let index = 0; index < localStorage.length; index = index + 1) {
      const key = localStorage.key(index)
      if (key !== null && key.startsWith(CACHE_PREFIX) === true) {
        allKeys.push(key)
      }
    }

    if (allKeys.length <= CACHE_MAX_KEYS) {
      return
    }

    const sortable = allKeys.map(key => {
      try {
        const raw = localStorage.getItem(key) ?? ''
        const parsed = JSON.parse(raw) as LeaseCheckCacheEntry
        return { key, generatedAt: parsed.generatedAt }
      } catch (error) {
        console.error('[lease-check] cache read failed', error)
        return { key, generatedAt: 0 }
      }
    })

    sortable.sort((alpha, beta) => alpha.generatedAt - beta.generatedAt)

    const overflow = sortable.length - CACHE_MAX_KEYS
    for (let index = 0; index < overflow; index = index + 1) {
      localStorage.removeItem(sortable[index].key)
    }
  } catch (error) {
    console.error('[lease-check] cache trim failed', error)
  }
}

const readCache = (fingerprint: string): LeaseCheckCacheEntry | null => {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${fingerprint}`)
    if (raw === null) {
      return null
    }
    const parsed = JSON.parse(raw) as LeaseCheckCacheEntry
    if (Date.now() - parsed.generatedAt > CACHE_TTL_MS) {
      localStorage.removeItem(`${CACHE_PREFIX}${fingerprint}`)
      return null
    }
    return parsed
  } catch (error) {
    console.error('[lease-check] cache read failed', error)
    return null
  }
}

const writeCache = (
  fingerprint: string,
  result: LeaseClauseCheckResult,
): void => {
  try {
    const entry: LeaseCheckCacheEntry = {
      fingerprint,
      result,
      generatedAt: Date.now(),
    }
    localStorage.setItem(`${CACHE_PREFIX}${fingerprint}`, JSON.stringify(entry))
    trimCacheToMax()
  } catch (error) {
    console.error('[lease-check] cache write failed', error)
  }
}

const riskTone = (score: number): LeaseClauseSeverity => {
  if (score <= RISK_GREEN_MAX) {
    return 'green'
  }
  if (score <= RISK_YELLOW_MAX) {
    return 'yellow'
  }
  return 'red'
}

const riskLabel = (score: number): string => {
  if (score <= RISK_GREEN_MAX) {
    return 'Looks clean'
  }
  if (score <= RISK_YELLOW_MAX) {
    return 'Worth negotiating'
  }
  return 'Take a hard look'
}

const severityGlyph = (severity: LeaseClauseSeverity): IconDefinition => {
  if (severity === 'red') {
    return faFlag
  }
  if (severity === 'yellow') {
    return faTriangleExclamation
  }
  return faCheck
}

const sortFindingsBySeverity = (
  findings: LeaseClauseCheckResult['findings'],
): LeaseClauseCheckResult['findings'] => {
  const order: Record<LeaseClauseSeverity, number> = {
    red: 0,
    yellow: 1,
    green: 2,
  }
  return [...findings].sort(
    (alpha, beta) => order[alpha.severity] - order[beta.severity],
  )
}

export {
  JURISDICTION_OPTIONS,
  buildFingerprint,
  readCache,
  writeCache,
  riskTone,
  riskLabel,
  severityGlyph,
  sortFindingsBySeverity,
}
